import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { fmtBtc, fmtCompactBtc, fmtCompactUsd } from "./utils";
import { getDefiApi } from "./getDefiApi";
import Seo from "./Seo";
import { routeMeta } from "./routeMeta";
import PageHeading from "./PageHeading";
import { PageContainer } from "./sharedStyleds";

// Half an hour, matching the function's cache window. Polling faster would only
// re-read the same cached body: custody balances move when somebody mints or
// redeems, not continuously.
const REFRESH_MS = 30 * 60 * 1000;

const NARROW = "650px";

const Headline = styled.div`
  margin: 0 0 1.4rem;
`;

// BTC is the unit this page is denominated in, so it gets the size and the
// dollars ride along in brackets. On a Bitcoin site the dollar figure is the
// translation, not the number - every other DeFi dashboard leads with dollars
// and this one deliberately does not.
const BigBtc = styled.div`
  font-size: 2.1rem;
  font-weight: 700;
  line-height: 1.15;

  @media (max-width: ${NARROW}) {
    font-size: 1.7rem;
  }
`;

const BigUsd = styled.span`
  font-size: 1.1rem;
  font-weight: 400;
  color: var(--secondary-color);
  margin-left: 0.5rem;

  @media (max-width: ${NARROW}) {
    font-size: 0.95rem;
  }
`;

const Caption = styled.p`
  margin: 0.35rem 0 0;
  color: var(--secondary-color);
  max-width: 70ch;
`;

const SectionHeading = styled.h2`
  font-size: 1.15rem;
  margin: 2rem 0 0.15rem;
`;

const SectionNote = styled.p`
  margin: 0 0 0.9rem;
  color: var(--secondary-color);
  max-width: 75ch;
  font-size: 0.92rem;
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  min-width: 560px;
  margin: 0;
  border-collapse: collapse;

  td,
  th {
    border: 1px solid var(--code-bg-color);
    padding: 0.55rem 10px;
    vertical-align: middle;
  }

  th {
    font-size: 1rem;
    text-align: left;
    white-space: nowrap;
  }

  @media (max-width: ${NARROW}) {
    min-width: 0;

    td,
    th {
      padding: 0.5rem 6px;
    }

    .wide {
      display: none;
    }
  }
`;

const NumCell = styled.td`
  font-weight: 100;
  color: var(--secondary-color);
  white-space: nowrap;
  text-align: right;
`;

// The BTC column is the one being compared, so it keeps full weight while the
// dollars beside it recede.
const BtcCell = styled(NumCell)`
  font-weight: 400;
  color: inherit;
`;

const Name = styled.div`
  font-weight: 700;
  line-height: 1.2;
`;

// The kind has its own column on a wide screen, where that column is dropped it
// folds under the name instead. Hidden by default rather than shown, so the two
// can never both appear.
const Category = styled.div`
  font-size: 0.8rem;
  font-weight: 100;
  color: var(--secondary-color);
  line-height: 1.2;
  display: none;

  @media (max-width: ${NARROW}) {
    display: block;
  }
`;

const Status = styled.p`
  color: var(--secondary-color);
`;

// The prerender step writes these answers into the served HTML, so the page has
// to render the same words - text put in front of a crawler that a reader never
// sees is cloaking. routeMeta is the single copy for exactly that reason.
//
// No FAQPage structured data: Google retired FAQ rich results on 7 May 2026, so
// the markup would produce nothing. The answers earn their place as prose, for a
// reader who does not know what a wrapped Bitcoin is and for the assistants that
// summarise pages by quoting them.
const Faq = styled.section`
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px dotted #3f3f44;
  max-width: 75ch;
`;

const FaqTitle = styled.h3`
  margin-bottom: 0.2rem;
  font-size: 1rem;
`;

const Question = styled.h4`
  margin: 1rem 0 0.2rem;
  font-size: 0.95rem;
`;

const Answer = styled.p`
  margin: 0;
  font-size: 0.88rem;
  color: #a3abba;
`;

const Source = styled.p`
  margin-top: 1.6rem;
  font-size: 0.85rem;
  color: var(--secondary-color);
  max-width: 75ch;
`;

const asOfText = (asOf) => {
  if (!asOf) return null;
  const when = new Date(asOf * 1000);
  if (Number.isNaN(when.getTime())) return null;
  return `${when.toISOString().slice(0, 16).replace("T", " ")} UTC`;
};

const Defi = () => {
  // null until the first answer, so "still loading" and "genuinely nothing"
  // can say different things rather than both rendering an empty table.
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const doFetch = async () => {
      const next = await getDefiApi();
      setFailed(next === null);
      // A failed refresh leaves what is on screen alone. Figures half an hour
      // old beat an empty page.
      if (next !== null) setData(next);
    };

    doFetch();
    const intervalId = setInterval(doFetch, REFRESH_MS);
    return () => clearInterval(intervalId);
  }, []);

  const custody = data?.custody ?? [];
  const chains = data?.chains ?? [];

  let statusMessage = null;
  if (failed && data === null) {
    statusMessage = "DeFi figures are temporarily unavailable. Trying again shortly.";
  } else if (data === null) {
    statusMessage = "Loading DeFi figures...";
  }

  const measuredAt = asOfText(data?.asOf);

  return (
    <PageContainer>
      <Seo {...routeMeta["/defi"]} path="/defi" />
      <PageHeading meta={routeMeta["/defi"]} />

      {statusMessage ? (
        <Status>{statusMessage}</Status>
      ) : (
        <>
          <Headline>
            <BigBtc>
              {fmtCompactBtc(data.btcTotal)} BTC
              <BigUsd>({fmtCompactUsd(data.usdTotal)})</BigUsd>
            </BigBtc>
            <Caption>
              Bitcoin held on the Bitcoin chain by the {custody.length} largest
              protocols that issue a wrapped or staked version of it elsewhere.
              {measuredAt ? ` Measured ${measuredAt}.` : ""}
            </Caption>
          </Headline>

          <SectionHeading>Who holds it</SectionHeading>
          <SectionNote>
            Each row is BTC sitting on Bitcoin, backing a token on another chain.
            Only Bitcoin-chain balances are counted, so a protocol holding WBTC
            on Ethereum is not counted twice — once here and once inside WBTC's
            own row.
          </SectionNote>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th>Protocol</th>
                  <th className="wide">Kind</th>
                  <th style={{ textAlign: "right" }}>BTC held</th>
                  <th style={{ textAlign: "right" }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {custody.map((row) => (
                  <tr key={row.slug}>
                    <td>
                      <Name>{row.name}</Name>
                      <Category>{row.category}</Category>
                    </td>
                    <NumCell className="wide" style={{ textAlign: "left" }}>
                      {row.category ?? "—"}
                    </NumCell>
                    <BtcCell>{fmtBtc(row.btc)}</BtcCell>
                    <NumCell>{fmtCompactUsd(row.usd)}</NumCell>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>

          <SectionHeading>Bitcoin's own DeFi</SectionHeading>
          <SectionNote>
            Total value locked on chains that settle to or bridge from Bitcoin,
            next to Bitcoin itself. These are mixed assets rather than BTC, so
            they are shown in dollars — restating them in BTC would be a
            division dressed up as a measurement. Which chains count as
            Bitcoin's is a judgement, and this is a hand-kept list.
          </SectionNote>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th>Chain</th>
                  <th style={{ textAlign: "right" }}>Total value locked</th>
                </tr>
              </thead>
              <tbody>
                {chains.map((chain) => (
                  <tr key={chain.name}>
                    <td>
                      <Name>{chain.name}</Name>
                    </td>
                    <NumCell>{fmtCompactUsd(chain.usd)}</NumCell>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>

          <Source>
            Figures from DefiLlama, which measures them; this page selects and
            arranges them. A custody balance is what a protocol reports holding,
            not a proof of reserve — d2index has not verified the addresses
            behind these numbers.
          </Source>
        </>
      )}

      {/* Outside the block above on purpose. These answers do not depend on the
          figures, so they should be on the page while it is still loading and
          when the fetch has failed - which is also when a reader most needs
          something to read. */}
      <Faq>
        <FaqTitle>Common questions</FaqTitle>
        {routeMeta["/defi"].faq.map(({ question, answer }) => (
          <div key={question}>
            <Question>{question}</Question>
            <Answer>{answer}</Answer>
          </div>
        ))}
      </Faq>
    </PageContainer>
  );
};

export default Defi;
